// utils/jwtUtils.ts - 新建文件
// 统一的JWT生成工具，替换所有mock token

/**
 * 生成标准JWT格式的Token
 * @param userId 用户ID
 * @param tokenType token类型：access 或 refresh
 * @param extraClaims 额外的claims
 */
export function generateJWT(
    userId: string,
    tokenType: 'access' | 'refresh' = 'access',
    extraClaims: Record<string, any> = {}
): string {

    const header = {
        alg: "HS256",
        typ: "JWT"
    }

    const now = Math.floor(Date.now() / 1000)
    const expiration = tokenType === 'refresh'
        ? now + (30 * 24 * 60 * 60)  // refresh token 30天
        : now + (24 * 60 * 60)       // access token 24小时

    const payload = {
        sub: userId,
        iat: now,
        exp: expiration,
        jti: `${tokenType}_${Date.now()}_${Math.random().toString(36).substring(2)}`, // JWT ID
        userId: userId,
        tokenType: tokenType,
        iss: "lottery-app",
        aud: "lottery-game",
        // 默认用户信息
        username: userId.includes('mock') || userId.includes('dev') ? '测试用户' : userId,
        balance: 1688.88,
        vipLevel: "VIP 3",
        // 合并额外的claims
        ...extraClaims
    }

    // 安全的Base64编码
    const safeBase64Encode = (str: string): string => {
        try {
            return btoa(unescape(encodeURIComponent(str)))
        } catch (e) {
            // 如果运行环境不支持btoa，使用替代方案
            console.warn('btoa不可用，使用替代编码方案')
            return Buffer.from(str, 'utf8').toString('base64')
        }
    }

    const encodedHeader = safeBase64Encode(JSON.stringify(header))
    const encodedPayload = safeBase64Encode(JSON.stringify(payload))

    // 生成签名（在开发环境中使用伪签名）
    const signaturePayload = `${encodedHeader}.${encodedPayload}.secret_key_${Date.now()}`
    const signature = safeBase64Encode(signaturePayload.substring(0, 32)) // 截取固定长度

    return `${encodedHeader}.${encodedPayload}.${signature}`
}

/**
 * 解析JWT并返回payload（仅用于开发调试）
 */
export function parseJWTPayload(jwt: string): any {
    try {
        const parts = jwt.split('.')
        if (parts.length !== 3) {
            throw new Error('Invalid JWT format')
        }

        const payload = parts[1]
        const decoded = atob(payload)
        return JSON.parse(decoded)
    } catch (e) {
        console.error('JWT解析失败:', e)
        return null
    }
}

/**
 * 验证JWT格式是否正确
 */
export function isValidJWTFormat(token: string): boolean {
    if (!token || typeof token !== 'string') return false

    const parts = token.split('.')
    if (parts.length !== 3) return false

    // 检查每部分是否为有效的base64
    try {
        atob(parts[0]) // header
        atob(parts[1]) // payload
        atob(parts[2]) // signature
        return true
    } catch (e) {
        return false
    }
}

/**
 * 生成用户相关的JWT对
 */
export function generateTokenPair(userId: string, userInfo: any = {}) {
    const accessToken = generateJWT(userId, 'access', {
        username: userInfo.username || '用户',
        balance: userInfo.balance || 1688.88,
        vipLevel: userInfo.vipLevel || 'VIP 1'
    })

    const refreshToken = generateJWT(userId, 'refresh', {
        username: userInfo.username || '用户'
    })

    return {
        accessToken,
        refreshToken,
        expiresIn: 24 * 60 * 60 * 1000, // 24小时毫秒数
        tokenType: 'Bearer'
    }
}

export default {
    generateJWT,
    parseJWTPayload,
    isValidJWTFormat,
    generateTokenPair
}